import { z } from 'zod';
export const IdeationWidgetKey = 'ideation-widget:v2';
export const IdeationWidgetKind = 'ideation';
// Key prefixes for saved ideation artifacts; the Library preview routes by these.
export const SAVED_IDEATION_CAMPAIGN_ARTIFACT_KEY_PREFIX = 'saved-ideation-campaign:';
export const SAVED_IDEATION_IDEA_ARTIFACT_KEY_PREFIX = 'saved-ideation-idea:';
// Guided-mode scratch keys. These artifacts back the agent's own state
// management (isWidget:false, kind:'ideation') and are NOT user-facing, so both
// the workspace picker and the project Library grid exclude them by prefix. They
// live here (not in apps/api) so workspace-api can import the same constants.
export const IdeationContextKey = 'ideation-context:v1';
export const IdeationIntermediateStateKey = 'ideation-intermediate-state:v1';
// Prefixes (not exact keys) so a future `:v2` scratch artifact is covered
// automatically by the listing-exclusion queries.
export const IDEATION_CONTEXT_ARTIFACT_KEY_PREFIX = 'ideation-context:';
export const IDEATION_INTERMEDIATE_STATE_ARTIFACT_KEY_PREFIX = 'ideation-intermediate-state:';
export const CoreTaskSchema = z
    .enum(['All in', 'Stunt', 'AO/Quick Wins', 'IRL Activations'])
    .describe('Type of briefing or task');
export const IdeationKeyStrategicElementsSchema = z.object({
    title: z
        .enum(['Core Tension', 'Key Message', 'Audience'])
        .describe('The title of the key strategic element'),
    content: z.string().describe('The content of the key strategic element'),
});
export const IdeationConstraintsSchema = z.object({
    title: z.string().describe('The title of the constraint'),
    content: z.string().describe('The content of the constraint'),
});
export const IdeationBenchmarkReferenceSchema = z.object({
    type: z.enum(['cliches', 'disruptive-stunt']).describe('The creative archetype of the campaign'),
    title: z.string().describe('The commonly recognized name or title of the campaign.'),
    description: z
        .string()
        .describe('A very short concise summary of what the campaign did, how it worked, and its core creative approach.'),
    universalTruth: z
        .string()
        .describe('The foundational human insight or emotional truth that the idea is built on'),
    brand: z.string().describe('The brand, organization, or entity behind the campaign.'),
    agency: z
        .string()
        .describe('The creative agency or team responsible for developing the campaign.'),
    year: z.string().describe('The year the campaign launched.'),
    link: z.string().describe('Verifiable link to the campaign'),
});
export const IdeationBenchmarkReferenceWithImageSchema = z.object({
    ...IdeationBenchmarkReferenceSchema.shape,
    imageAssetId: z.string().describe('Unique identifier for the asset'),
    imageMimeType: z.string().describe('MIME type of the asset, e.g., image/png'),
    imageFileName: z.string().describe('Original filename of the asset'),
    imageSize: z.number().describe('Size of the asset in bytes'),
});
export const IdeationExecutionSchema = z.object({
    title: z.string().describe('The title of the execution'),
    description: z.string().describe('The description of the execution'),
    platform: z.string().describe('The platform of the execution'),
});
export const IdeationExecutionWithImagesSchema = z.object({
    ...IdeationExecutionSchema.shape,
    imageAssetId: z.string().describe('Unique identifier for the asset'),
    imageMimeType: z.string().describe('MIME type of the asset, e.g., image/png'),
    imageFileName: z.string().describe('Original filename of the asset'),
    imageSize: z.number().describe('Size of the asset in bytes'),
});
// Score title enums for reuse in agents
export const AudienceScoreTitleEnum = z.enum([
    'General',
    'Emotional',
    'Cultural',
    'Purpose',
    'Sustained interest',
]);
export const CreativeScoreTitleEnum = z.enum([
    'General',
    'Innovation',
    'Creativity',
    'Longevity',
    'Risk taking',
]);
export const IdeationAudienceScoreSchema = z.object({
    title: AudienceScoreTitleEnum.describe('The title of the score'),
    score: z.number().int().min(10).max(100).describe('The score value (10-100)'),
    description: z.string().describe('The description of the score'),
});
export const IdeationAudienceScoreListSchema = z.array(IdeationAudienceScoreSchema).length(5);
export const IdeationCreativeScoreSchema = z.object({
    title: CreativeScoreTitleEnum.describe('The title of the score'),
    score: z.number().int().min(10).max(100).describe('The score value (10-100)'),
    description: z.string().describe('The description of the score'),
});
export const IdeationCreativeScoreListSchema = z.array(IdeationCreativeScoreSchema).length(5);
export const IdeationIdeaSchema = z.object({
    id: z.string().describe('The id of the idea (UUID)'),
    title: z.string().describe('The title of the idea'),
    description: z.string().describe('The description of the idea'),
    provocation: z
        .string()
        .describe('A very short summary of the actionable provocation spark that this idea is based on'),
    whyThisWorks: z.string().describe('The why this works of the idea'),
    executions: z.array(IdeationExecutionSchema).describe('The executions of the idea'),
});
export const IdeationIdeaWithoutExecutionsSchema = z.object({
    id: z.string().describe('The id of the idea (UUID)'),
    title: z.string().describe('The title of the idea'),
    description: z.string().describe('The description of the idea'),
    provocation: z
        .string()
        .describe('A very short summary of the actionable provocation spark that this idea is based on'),
    whyThisWorks: z.string().describe('The why this works of the idea'),
});
export const IdeationIdeaWithScoreSchema = z.object({
    ...IdeationIdeaSchema.shape,
    audienceScore: IdeationAudienceScoreListSchema.describe('The audience score of the idea'),
    creativeScore: IdeationCreativeScoreListSchema.describe('The creative score of the idea'),
});
export const IdeationIdeaWithImagesSchema = z.object({
    ...IdeationIdeaWithScoreSchema.shape,
    imageAssetId: z.string().describe('Unique identifier for the asset'),
    imageMimeType: z.string().describe('MIME type of the asset, e.g., image/png'),
    imageFileName: z.string().describe('Original filename of the asset'),
    imageSize: z.number().describe('Size of the asset in bytes'),
    executions: z.array(IdeationExecutionWithImagesSchema).describe('The executions of the idea'),
});
const IdeationUnconventionalSparkTopicEnum = z.enum([
    'Reframe',
    'Counter-truth',
    'Metaphorical Comparison',
]);
// Structured-output models occasionally emit the enum definition itself as an
// array (e.g. ["Reframe","Counter-truth","Metaphorical Comparison"]) or a
// single-element array like ["Reframe"]. Coerce to the first element so parse
// succeeds instead of hard-failing an entire ideation run. The JSON schema
// exposed to the LLM still advertises the plain enum, so this only kicks in
// when the model regresses.
const IdeationUnconventionalSparkTopicSchema = z.preprocess((value) => {
    if (Array.isArray(value) && value.length > 0) {
        return value[0];
    }
    return value;
}, IdeationUnconventionalSparkTopicEnum);
export const IdeationUnconventionalSparkSchema = z.object({
    topic: IdeationUnconventionalSparkTopicSchema.describe('The provocative type'),
    question: z.string().describe('The provocative question'),
    answer: z.string().describe('A concise, actionable answer based on your internal reasoning'),
});
export const IdeationImageSchema = z.object({
    assetId: z.string().describe('Unique identifier for the asset'),
    mimeType: z.string().describe('MIME type of the asset, e.g., image/png'),
    fileName: z.string().describe('Original filename of the asset'),
    size: z.number().describe('Size of the asset in bytes'),
});
export const IdeationWidgetSchema = z.object({
    title: z.string().describe('The title of the ideation widget'),
    coreTask: CoreTaskSchema.describe('The core task of the ideation widget'),
    creativeMission: z.string().describe('The creative mission of the ideation widget'),
    keyStrategicElements: z
        .array(IdeationKeyStrategicElementsSchema)
        .describe('The key strategic elements of the ideation widget'),
    constraints: z
        .array(IdeationConstraintsSchema)
        .describe('The constraints of the ideation widget'),
    imageAssetId: z.string().describe('Unique identifier for the asset'),
    imageMimeType: z.string().describe('MIME type of the asset, e.g., image/png'),
    imageFileName: z.string().describe('Original filename of the asset'),
    imageSize: z.number().describe('Size of the asset in bytes'),
    additionalImages: z
        .array(IdeationImageSchema)
        .describe('The additional images of the ideation widget'),
    references: z
        .array(IdeationBenchmarkReferenceWithImageSchema)
        .describe('The benchmark references'),
    sparks: z.array(IdeationUnconventionalSparkSchema).min(1).describe('The unconventional sparks'),
    ideas: z.array(IdeationIdeaWithImagesSchema).min(1).describe('The ideas of the ideation widget'),
});
